import React from 'react';
import { FiBarChart2, FiCheckCircle, FiAlertCircle, FiInfo } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useFramework } from '../contexts/FrameworkContext';
import { Heading } from '../components/ui/heading';

interface TCFOutcome {
  id: string;
  title: string;
  description: string;
  complianceScore: number;
  status: 'compliant' | 'attention' | 'risk';
  complaints: number;
}

const TCFFramework: React.FC = () => {
  const { framework } = useFramework();
  
  // Mock TCF outcomes with compliance scores
  const tcfOutcomes: TCFOutcome[] = [
    {
      id: 'TCF1',
      title: 'Culture and Governance',
      description: 'Fair treatment is central to corporate culture.',
      complianceScore: 87,
      status: 'compliant',
      complaints: 12
    },
    {
      id: 'TCF2',
      title: 'Product Design',
      description: 'Products meet customer needs.',
      complianceScore: 92,
      status: 'compliant',
      complaints: 7
    },
    {
      id: 'TCF3',
      title: 'Clear Information',
      description: 'Customers receive clear, appropriate information.',
      complianceScore: 76,
      status: 'attention',
      complaints: 31
    },
    {
      id: 'TCF4',
      title: 'Suitable Advice',
      description: 'Advice considers customer circumstances.',
      complianceScore: 82,
      status: 'compliant',
      complaints: 15
    },
    {
      id: 'TCF5',
      title: 'Product Performance',
      description: 'Products perform as expected with acceptable service.',
      complianceScore: 65,
      status: 'risk',
      complaints: 42
    },
    {
      id: 'TCF6',
      title: 'No Unreasonable Barriers',
      description: 'No unreasonable post-sale barriers.',
      complianceScore: 79,
      status: 'attention',
      complaints: 26
    }
  ];
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <IconWrapper icon={FiCheckCircle} className="text-green-500" />;
      case 'attention':
        return <IconWrapper icon={FiInfo} className="text-amber-500" />;
      case 'risk':
        return <IconWrapper icon={FiAlertCircle} className="text-red-500" />;
      default:
        return null;
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'bg-green-50 border-green-200';
      case 'attention':
        return 'bg-amber-50 border-amber-200';
      case 'risk':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div>
      <div className="mb-8">
        <Heading level={1} weight="bold" className="text-3xl mb-2">TCF Framework</Heading>
        <p className="text-secondary/70">Treating Customers Fairly analysis and compliance metrics</p>
      </div>
      
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold mb-1">Overall TCF Compliance</h2>
              <p className="text-secondary/70">Based on complaint analysis and outcomes</p>
            </div>
            <div className="flex gap-1">
              <button className="border border-gray-200 text-secondary/70 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50">
                Last 30 Days
              </button>
              <button className="bg-primary/10 text-primary px-3 py-1.5 rounded-lg text-sm">
                Last Quarter
              </button>
              <button className="border border-gray-200 text-secondary/70 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50">
                Last Year
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-secondary/70 text-sm mb-1">Overall Score</div>
                  <div className="text-3xl font-bold">80.2%</div>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <IconWrapper icon={FiBarChart2} className="text-blue-600" size={24} />
                </div>
              </div>
              <div className="mt-4">
                <div className="h-2 w-full bg-blue-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 rounded-full"
                    style={{ width: '80.2%' }}
                  ></div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="text-secondary/70 text-sm mb-1">Total TCF-Related Complaints</div>
              <div className="text-3xl font-bold mb-3">133</div>
              <div className="text-xs text-secondary/70">
                <span className="text-red-500">+12.4%</span> compared to previous period
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="text-secondary/70 text-sm mb-1">Improvement Areas</div>
              <div className="text-lg font-medium mb-3">2 TCF Outcomes</div>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-0.5 bg-red-50 text-red-800 text-xs rounded-full inline-flex items-center justify-center">TCF Outcome 5</span>
                <span className="px-2 py-0.5 bg-amber-50 text-amber-800 text-xs rounded-full inline-flex items-center justify-center">TCF Outcome 3</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-bold mb-4">TCF Outcomes Analysis</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tcfOutcomes.map((outcome) => (
              <div 
                key={outcome.id}
                className={`border rounded-xl p-5 ${getStatusColor(outcome.status)}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-bold text-lg">{outcome.title}</h3>
                    <p className="text-secondary/70 text-sm">{outcome.description}</p>
                  </div>
                  <div>
                    {getStatusIcon(outcome.status)}
                  </div>
                </div>
                
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-secondary/70">Compliance Score</div>
                  <div className="font-medium">{outcome.complianceScore}%</div>
                </div>
                
                <div className="w-full h-1.5 bg-white rounded-full mb-3">
                  <div 
                    className={`h-full rounded-full ${
                      outcome.status === 'compliant' ? 'bg-green-500' : 
                      outcome.status === 'attention' ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${outcome.complianceScore}%` }}
                  ></div>
                </div>
                
                <div className="flex justify-between items-center">
                  <div className="text-sm">
                    <span className="font-medium">{outcome.complaints}</span>
                    <span className="text-secondary/70"> complaints</span>
                  </div>
                  <button className="text-primary text-sm hover:underline">
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TCFFramework;